import { defineStore } from 'pinia'
import { useLocalStorage } from '@vueuse/core'
import { ref } from 'vue'

type TimeString = `${number}${'m' | 'h' | 'd' | 'w' | 'M' | 'y'}`
type ChanceType = number | `${number}visit` | `${number}fo`

interface MarketingState {
  history: Record<string, string> // Ключ: ISO дата последнего успеха
  stats: Record<string, { visits: number, clicks: number, exits: number }>
}

interface InitOptions {
  interval: TimeString | number | (TimeString | number)[]
  chance: ChanceType | ChanceType[]
  meta?: {
    priority?: number
    clickSelector?: {
      selector: string
      threshold: number
    }
  }
}

interface ExecuteContext {
  key: string
  callback: () => void
  storageKey: string
  interval: TimeString | number
}

export type MarketingTrackCounter = 'clicks' | 'visits' | 'exits'
export type MarketingTrackSink = (key: string, field: MarketingTrackCounter) => void

export const useMarketingStore = defineStore('marketing', () => {
  const PREFIX = 'SHARED_MARKETING_'
  const QUEUE_INTERVAL_MS = 30000
  const MAX_QUEUE_RETRIES = 10

  const storage = useLocalStorage<MarketingState>('SHARED_MARKETING_CONFIG', {
    history: {},
    stats: {},
  })

  const lastGlobalEventTs = ref<number>(0)
  const pendingTimers = new Set<ReturnType<typeof setTimeout>>()
  const delegatedSelectors = new Map<string, (() => void)[]>()
  const initializedKeys = new Set<string>()

  let trackSink: MarketingTrackSink | null = null

  const setTrackSink = (sink: MarketingTrackSink | null) => {
    trackSink = sink
  }

  const hydrateState = (key: string, state: { clicks: number, visits: number, exits: number, lastShownAt?: number }) => {
    const storageKey = `${PREFIX}${key.toUpperCase()}`
    updateStorage((s) => {
      const current = s.stats[storageKey] || { visits: 0, clicks: 0, exits: 0 }
      s.stats[storageKey] = {
        visits: Math.max(current.visits, state.visits),
        clicks: Math.max(current.clicks, state.clicks),
        exits: Math.max(current.exits, state.exits),
      }
      if (state.lastShownAt) {
        const localTs = s.history[storageKey] ? new Date(s.history[storageKey] as string).getTime() : 0
        if (state.lastShownAt > localTs) {
          s.history[storageKey] = new Date(state.lastShownAt).toISOString()
        }
      }
    })
  }

  const updateStorage = (updater: (state: MarketingState) => void) => {
    updater(storage.value)
  }

  const getSeconds = (val: string | number): number => {
    if (typeof val === 'number') return val
    const unitMap: Record<string, number> = {
      m: 60,
      h: 60 * 60,
      d: 24 * 60 * 60,
      w: 7 * 24 * 60 * 60,
      M: 30 * 24 * 60 * 60,
      y: 365 * 24 * 60 * 60,
    }
    const match = val.match(/^(\d+)([mhdwMy])$/)
    return match ? Number.parseInt(match[1] as string) * (unitMap[match[2] as string] || 0) : 0
  }

  const getSecureRandom = (): number => {
    const array = new Uint32Array(1)
    window.crypto.getRandomValues(array)
    return array[0]! / 0x100000000
  }

  const pickRandom = <T extends number | string>(val: T | T[]): T => {
    if (!Array.isArray(val)) return val
    return val[Math.floor(getSecureRandom() * val.length)] as T
  }

  const canExecuteNow = () => Date.now() - lastGlobalEventTs.value >= QUEUE_INTERVAL_MS

  const isIntervalElapsed = (storageKey: string, interval: TimeString | number): boolean => {
    const lastInitStr = storage.value.history[storageKey]
    const lastInitTs = lastInitStr ? Math.floor(new Date(lastInitStr).getTime() / 1000) : 0
    return Math.floor(Date.now() / 1000) - lastInitTs >= getSeconds(interval)
  }

  const attemptExecute = (ctx: ExecuteContext, attempt = 0): boolean => {
    if (!isIntervalElapsed(ctx.storageKey, ctx.interval)) return false

    if (canExecuteNow()) {
      return execute(ctx.key, ctx.callback)
    }
    if (attempt >= MAX_QUEUE_RETRIES) {
      return false
    }
    const timer = setTimeout(() => {
      pendingTimers.delete(timer)
      attemptExecute(ctx, attempt + 1)
    }, 60 * 1000)
    pendingTimers.add(timer)
    return false
  }

  const cancelPending = () => {
    for (const timer of pendingTimers) {
      clearTimeout(timer)
    }
    pendingTimers.clear()
  }

  const registerDelegatedClick = (selector: string, handler: () => void) => {
    if (!delegatedSelectors.has(selector)) {
      delegatedSelectors.set(selector, [])
      document.addEventListener('click', (event) => {
        if (!(event.target as HTMLElement | null)?.closest(selector)) return
        for (const fn of delegatedSelectors.get(selector)!) {
          fn()
        }
      })
    }
    delegatedSelectors.get(selector)!.push(handler)
  }

  const tryInitialize = (key: string, callback: () => void, options: InitOptions): boolean => {
    if (import.meta.server) return false

    const activeInterval = pickRandom(options.interval)
    const activeChance = pickRandom(options.chance)
    const storageKey = `${PREFIX}${key.toUpperCase()}`

    if (!isIntervalElapsed(storageKey, activeInterval)) return false

    if (initializedKeys.has(storageKey)) return false
    initializedKeys.add(storageKey)

    if (!storage.value.stats[storageKey]) {
      updateStorage((s) => {
        s.stats[storageKey] = { visits: 0, clicks: 0, exits: 0 }
      })
    }

    const ctx: ExecuteContext = { key, callback, storageKey, interval: activeInterval }

    if (options.meta?.clickSelector) {
      const { selector, threshold } = options.meta.clickSelector
      registerDelegatedClick(selector, () => {
        updateStorage(s => s.stats[storageKey]!.clicks++)
        trackSink?.(key, 'clicks')
        if (storage.value.stats[storageKey]!.clicks >= threshold) {
          updateStorage(s => s.stats[storageKey]!.clicks = 0)
          attemptExecute(ctx)
        }
      })
      return false
    }

    let isQualified = false
    if (typeof activeChance === 'string') {
      if (activeChance.endsWith('fo')) {
        const limit = Number.parseInt(activeChance)

        const handleFocusOff = () => {
          if (storage.value.stats[storageKey]!.exits >= limit) {
            removeListeners()
            return
          }
          updateStorage(s => s.stats[storageKey]!.exits++)
          trackSink?.(key, 'exits')
          attemptExecute(ctx)

          if (storage.value.stats[storageKey]!.exits >= limit) {
            removeListeners()
          }
        }

        const handleBlur = () => handleFocusOff()
        const handleVisibilityChange = () => {
          if (document.hidden) handleFocusOff()
        }
        const removeListeners = () => {
          window.removeEventListener('blur', handleBlur)
          document.removeEventListener('visibilitychange', handleVisibilityChange)
        }

        window.addEventListener('blur', handleBlur)
        document.addEventListener('visibilitychange', handleVisibilityChange)
        return true
      }
      if (activeChance.endsWith('visit')) {
        updateStorage(s => s.stats[storageKey]!.visits++)
        trackSink?.(key, 'visits')
        if (storage.value.stats[storageKey]!.visits >= Number.parseInt(activeChance)) {
          updateStorage(s => s.stats[storageKey]!.visits = 0)
          isQualified = true
        }
      }
    } else if (typeof activeChance === 'number') {
      if (getSecureRandom() <= activeChance) isQualified = true
    }

    if (isQualified) return attemptExecute(ctx)
    return false
  }

  const execute = (key: string, callback: () => void): boolean => {
    lastGlobalEventTs.value = Date.now()
    callback()

    const storageKey = `${PREFIX}${key.toUpperCase()}`
    updateStorage((s) => {
      s.history[storageKey] = new Date().toISOString()
    })

    return true
  }

  return {
    tryInitialize,
    cancelPending,
    setTrackSink,
    hydrateState,
  }
})
